import { Component, computed, DestroyRef, effect, inject, linkedSignal, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment-timezone';
import { filter, tap } from 'rxjs';

import { KeyValueType, WeekDaysEnum } from '@models/common';
import { TenantSettingsEnum } from '@models/tenant';
import {
  NewScheduledDialogData,
  ScheduledEmail,
  ScheduledEmailAdditionalRoles,
  ScheduleFrequencyEnum,
  SendMailToEnum,
} from '@module-instance/scheduled-emails/interfaces';
import { ScheduledEmailService } from '@module-instance/scheduled-emails/scheduled-emails.service';
import {
  CipoDateTimeControl,
  CipoFieldTypes,
  CipoListControl,
  CipoTextControl,
} from '@shared/components/fields/common';
import { FROALA_TOOLBAR_BIG, FroalaConfig } from '@shared/components/froala/common';
import { FroalaComponent } from '@shared/components/froala/froala.component';
import { CipoFroalaOptions, CipoTributeOptions } from '@shared/components/froala/interfaces';
import { DATE_TIME_FORMATS } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule, CipoFieldsModule, CipoFormModule } from '@shared/modules';
import { ItvDateTimePipe, ItvTimePipe } from '@shared/pipes';
import { CipoValidators, NotificationService, UtilsService } from '@shared/services';
import { UserStore } from '@signal-store/index';
import { TenantStore } from '@signal-store/tenant.store';

@Component({
  selector: 'cipo-new-schedule-dialog',
  templateUrl: './new-schedule-dialog.component.html',
  imports: [
    FroalaComponent,
    CipoFormModule,
    CipoDialogModule,
    CipoFieldsModule,
    ItvTimePipe,
    ItvDateTimePipe,
    CipoDialogHeaderComponent,
  ],
})
export class NewScheduleDialogComponent implements OnInit {
  /** this is always defined, no need to check for undefined */
  data = inject<NewScheduledDialogData>(MAT_DIALOG_DATA);
  private utilsService = inject(UtilsService);
  readonly dialogRef = inject(MatDialogRef<ScheduledEmail>);
  private destroyRef = inject(DestroyRef);
  private scheduledEmailsService = inject(ScheduledEmailService);
  private tenantStore = inject(TenantStore);
  private userStore = inject(UserStore);
  private emailService = inject(ScheduledEmailService);
  private notificationService = inject(NotificationService);
  readonly FroalaConfig = FroalaConfig;

  additionalRoles = signal<ScheduledEmailAdditionalRoles[]>([]);
  modules = signal<KeyValueType<number>[]>([]);

  percentNearOverdue = computed(
    () => this.tenantStore.settings()?.find(s => s.id === TenantSettingsEnum.percent_near_overdue)?.value as number,
  );
  tenant12hourFormat = computed(() => this.userStore.calculatedUserSettings()?.timeFormat?.includes('A'));
  now = computed(() => moment.tz(this.userStore.userData().ianaTimeZoneId));
  todayString = computed(() => this.now().format(DATE_TIME_FORMATS.dateIso));
  defaultTime = computed(() =>
    moment.tz(`${this.todayString().split('T')[0]}T09:00:00`, this.userStore.userData().ianaTimeZoneId).toISOString(),
  );
  weekDay = linkedSignal(() => this.now().weekday() as WeekDaysEnum);

  WEEK_DAYS = WeekDaysEnum;
  isManual = signal(false);
  isDisabled = signal(false);

  froalaOptionsSubject: CipoFroalaOptions = {
    ...FROALA_TOOLBAR_BIG,
    heightMin: 0,
    heightMax: 50,
    toolbarInline: true,
  };
  tributeOptions = signal<CipoTributeOptions>({
    variables: {},
  });
  tributeOptionsLoaded = signal(false);

  defaultEmailBody =
    '<p><br></p><p><br></p><p style="text-align: center;"><span class="parse-variables" id="TenantLogo"><span class="print">{{TenantLogo}}</span><span class="no-print">Tenant Logo</span></span>&nbsp;</p><p style="text-align: center;">This automated email was sent on behalf of&nbsp;<span class="parse-variables" id="TenantName"><span class="print">{{TenantName}}</span><span class="no-print">Tenant Name</span></span> from CIPO.</p>';

  form = new FormGroup({
    jobName: new CipoTextControl(
      this.data.scheduledEmail?.jobName,
      {
        type: CipoFieldTypes.Text,
        label: 'general.table.name',
        readonly: !this.data.canUpdate,
      },
      Validators.required,
    ),
    description: new CipoTextControl(
      this.data.scheduledEmail?.description,
      {
        type: CipoFieldTypes.Text,
        label: 'general.table.description',
        readonly: !this.data.canUpdate,
      },
      Validators.required,
    ),
    scheduleFrequency: new CipoListControl(
      this.data.scheduledEmail?.scheduleFrequency ?? ScheduleFrequencyEnum.WEEKLY,
      {
        type: CipoFieldTypes.Select,
        label: 'SCHEDULED_EMAILS.frequency.label',
        required: true,
        readonly: !this.data.canUpdate,
        options: this.utilsService
          .getEnumKeyValuePairs(ScheduleFrequencyEnum, ({ value }) => 'SCHEDULED_EMAILS.frequency.' + value)
          .filter(({ key }) => (key as unknown as ScheduleFrequencyEnum) !== ScheduleFrequencyEnum.MANUAL),
      },
    ),
    startDate: new CipoDateTimeControl(
      this.data.scheduledEmail?.startDate || this.todayString(),
      {
        type: CipoFieldTypes.DateTime,
        pickerType: 'date',
        preventClearInput: true,
        required: true,
        readonly: !this.data.canUpdate,
        label: 'general.table.startDate',
        minDate: this.todayString(),
      },
      Validators.required,
    ),
    timeOfDay: new CipoDateTimeControl(
      this.getTime(this.data.scheduledEmail?.timeOfDay),
      {
        type: CipoFieldTypes.DateTime,
        pickerType: 'time',
        hour12Timer: this.tenant12hourFormat(),
        preventClearInput: true,
        required: true,
        readonly: !this.data.canUpdate,
        label: 'general.table.time',
      },
      Validators.required,
    ),
    subject: new FormControl(this.data.scheduledEmail?.jobAttributes?.subject || '', Validators.required),
    emailBody: new FormControl(
      this.data.scheduledEmail?.jobAttributes?.emailBody || this.defaultEmailBody,
      Validators.required,
    ),
    moduleIds: new CipoListControl(
      this.data.scheduledEmail?.moduleIds || [],
      {
        type: CipoFieldTypes.Select,
        multiple: true,
        required: true,
        readonly: !this.data.canUpdate,
        label: 'general.modules',
      },
      Validators.required,
    ),
    sendTo: new CipoListControl(
      this.data.scheduledEmail?.sendTo || [SendMailToEnum.BIC_ROLE],
      {
        type: CipoFieldTypes.Select,
        multiple: true,
        label: 'SCHEDULED_EMAILS.sendEmailTo.label',
        readonly: !this.data.canUpdate,
        options: this.utilsService.getEnumKeyValuePairs(
          SendMailToEnum,
          ({ value }) => 'SCHEDULED_EMAILS.sendEmailTo.' + value,
        ),
      },
      CipoValidators.requireMinOne,
    ),
    additionalRecipients: new CipoListControl(this.data.scheduledEmail?.additionalRecipients || [], {
      type: CipoFieldTypes.Select,
      multiple: true,
      readonly: !this.data.canUpdate,
      label: 'SCHEDULED_EMAILS.onlySendTo',
      options: [],
    }),
  });

  disableChanged = effect(() => {
    if (this.isDisabled()) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  });

  ngOnInit(): void {
    this.scheduledEmailsService.getPlaceholders().subscribe(variables => {
      this.tributeOptions.set({ variables });
      this.tributeOptionsLoaded.set(true);
      this.populateDropdownData();
      this.handleFrequency();
    });
  }

  private getTime(timeOfDay: string): string {
    if (!timeOfDay) {
      return this.defaultTime();
    }
    return moment(`${this.todayString().split('T')[0]}T${timeOfDay}Z`).toISOString();
  }

  private populateDropdownData() {
    const { sendTo, additionalRecipients, moduleIds, scheduleFrequency, startDate, timeOfDay } = this.form.controls;
    this.scheduledEmailsService.getAdditionalRoles().subscribe(r => {
      this.additionalRoles.set(r.sort((a, b) => a.value.localeCompare(b.value)));
    });
    this.scheduledEmailsService.getModulesDropdownItems().subscribe(m => {
      this.modules.set(m.sort((a, b) => a.value.localeCompare(b.value)));
      const moduleKeys: number[] = m.map(({ key }) => key);
      if (!this.data.scheduledEmail) {
        moduleIds.setValue(moduleKeys, { emitEvent: false });
      }
    });

    // form is readonly because the scheduled email is active
    this.isDisabled.set(this.data.scheduledEmail?.isEnabled === true);

    sendTo.valueChanges
      .pipe(
        tap(
          val =>
            !val.includes(SendMailToEnum.DOCUMENT_INITIATOR) &&
            additionalRecipients.disabled &&
            !this.isDisabled() &&
            additionalRecipients.enable(),
        ),
        filter(val => val.includes(SendMailToEnum.DOCUMENT_INITIATOR)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        additionalRecipients.reset();
        additionalRecipients.disable();
      });

    // Handle sendTo initial state
    if (sendTo.value?.includes(SendMailToEnum.DOCUMENT_INITIATOR)) {
      additionalRecipients.reset();
      additionalRecipients.disable();
    }

    // Validate all fields to ensure errors are displayed
    Object.values(this.form.controls).forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });

    // Set default values only if this is a new scheduled email
    if (!this.data.scheduledEmail) {
      startDate.setValue(this.todayString());
      timeOfDay.setValue(this.defaultTime());
    }

    this.handleFrequencyChange(scheduleFrequency.value);
  }

  private handleFrequency() {
    const { scheduleFrequency, startDate } = this.form.controls;

    scheduleFrequency.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(frequency => this.handleFrequencyChange(frequency));

    startDate.valueChanges
      .pipe(
        filter(() => scheduleFrequency.value === ScheduleFrequencyEnum.WEEKLY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(d => this.setWeekDay(d ? (d as string) : undefined));
  }

  private handleFrequencyChange(frequency: ScheduleFrequencyEnum) {
    const { startDate, timeOfDay } = this.form.controls;

    this.isManual.set(frequency === ScheduleFrequencyEnum.MANUAL);

    if (frequency === ScheduleFrequencyEnum.MANUAL) {
      this.form.patchValue({
        startDate: null,
        timeOfDay: null,
      });
      startDate.disable();
      timeOfDay.disable();
      this.weekDay.set(undefined);
      return;
    }

    // make sure these are enabled if not manual in edit mode
    if (!this.isDisabled()) {
      startDate.enable();
      timeOfDay.enable();
    }

    this.setWeekDay(
      frequency === ScheduleFrequencyEnum.WEEKLY && startDate.value ? (startDate.value as string) : undefined,
    );
  }

  private setWeekDay(startDate: string) {
    if (!startDate) {
      this.weekDay.set(undefined);
      return;
    }
    return this.weekDay.set(moment(startDate.split('T')[0]).weekday());
  }

  saveChanges(sendNow = false) {
    const { emailBody, subject, timeOfDay, startDate, ...formProps } = this.form.value;
    let data: ScheduledEmail;
    const time = (timeOfDay as string).split('T')[1].replace('Z', ''); // get only the time part HH:mm:ss
    if (this.data.scheduledEmail) {
      const { frequency, ...dataProps } = this.data.scheduledEmail;
      data = {
        ...(dataProps as ScheduledEmail),
        ...(formProps as ScheduledEmail),
        startDate: startDate as string,
        timeOfDay: time,
        dayOfWeek: dataProps.dayOfWeek ?? this.weekDay(),
        jobAttributes: {
          ...this.data.scheduledEmail?.jobAttributes,
          emailBody,
          subject,
        },
      };
    } else {
      const { additionalRecipients, jobName, description, moduleIds, scheduleFrequency, sendTo } =
        formProps as ScheduledEmail;
      data = {
        ...(this.weekDay() && { dayOfWeek: this.weekDay() }),
        ...(sendNow && { sendNow }),
        jobName,
        description,
        additionalRecipients,
        moduleIds,
        scheduleFrequency,
        sendTo,
        startDate: startDate as string,
        timeOfDay: time,
        jobType: 1,
        jobAttributes: {
          emailBody,
          subject,
        },
      };
    }

    // disable the form to prevent multiple submits
    this.isDisabled.set(true);

    // create or update based on if we have an id
    const isUpdate = data.id;
    const req = isUpdate ? this.emailService.updateScheduledEmail(data) : this.emailService.createScheduledEmail(data);
    req.subscribe({
      next: () => {
        this.notificationService.success(
          isUpdate ? 'SCHEDULED_EMAILS.notifications.updated' : 'SCHEDULED_EMAILS.notifications.created',
        );

        // close the dialog and return the data
        this.dialogRef.close(data);
      },
      // enable the form if there was an error
      error: () => this.isDisabled.set(false),
    });
  }
}
